import QRCode from 'react-qr-code';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AR_VIEWER_ROUTE } from '../../constants/routePathConstants';
import {
  apiCreatePublicUUID,
  apiGetPublicUUID,
  apiDeletePublicUUID,
  apiGetPublicUUIDPublic,
  apiCreatePublicUUIDPublic,
} from '../../helpers/api';
import { isMobile } from 'react-device-detect';
import icons from '../../assets';
import Button from '../../components/Button';
import Loader from '../Loader';

function ARQRcode({
  propId,
  propName,
  isPublicPage,
  className = '',
  loading,
  orgProductUniqueCodeId = null,
  showLoading = false,
  customButtonText = null,
  customButtonStyle = null,
  qrCodeSize = '',
}) {
  const { t } = useTranslation('common');
  const [publicUUID, setPublicUUID] = useState(false);
  const [arLoading, setArLoading] = useState(false);

  useEffect(() => {
    if (orgProductUniqueCodeId && !publicUUID) {
      setPublicUUID(orgProductUniqueCodeId);
    }
  }, [orgProductUniqueCodeId]);

  const { ARViewerIcon } = icons;

  const searchParams = new URLSearchParams(window.location.search);
  const langParam = searchParams.get('lang');
  const langSuffix = langParam ? `?lang=${langParam}` : '';
  const BASE_URL = `${window.location.origin}${AR_VIEWER_ROUTE}`;
  const AR_VIEWER_URL = `${BASE_URL}?prop-id=${propId}${langParam ? `&lang=${langParam}` : ''}`;

  const handleToggleChange = async () => {
    if (!publicUUID) {
      const formData = new FormData();
      formData.append('product', propId);
      const api = isPublicPage ? apiCreatePublicUUIDPublic : apiCreatePublicUUID;
      const response = await api(formData);
      if (response?.status) {
        setPublicUUID(response?.data?.unique_code);
      }
    } else {
      const response = await apiDeletePublicUUID(publicUUID);
      if (response?.status) setPublicUUID('');
    }
  };

  const fetchARAPI = async () => {
    showLoading && setArLoading(true);
    const api = isPublicPage ? apiGetPublicUUIDPublic : apiGetPublicUUID;
    const response = await api(`?product=${propId}`);
    if (response?.results?.length) {
      setPublicUUID(response.results[0]?.unique_code);
    } else {
      handleToggleChange();
    }
    showLoading && setArLoading(false);
  };

  useEffect(async () => {
    if (!orgProductUniqueCodeId && !loading) {
      fetchARAPI();
    }
  }, [loading]);

  return (
    <div
      className={`d-flex flex-column gap-2 ${className}`}
      role="img"
      aria-label={`QR code for ${propName || 'AR viewer'}`}>
      {showLoading && arLoading ? <Loader loading={true} isV2={true} bgOpacity={0.75} /> : null}
      <QRCode
        value={publicUUID ? `${BASE_URL}${publicUUID}${langSuffix}` : AR_VIEWER_URL}
        title={propName || ''}
        level="Q"
        role="img"
        aria-label={`QR code to view ${propName || 'product'} in AR`}
        {...(qrCodeSize ? { style: { width: qrCodeSize, height: qrCodeSize } } : { size: 150 })}
      />
      {isMobile ? (
        <Button
          className={`${customButtonStyle?.color ? '' : 'lf-btn-1 text-white'} ${
            customButtonStyle?.padding ? 'custom-button-padding' : ''
          }`}
          iconBefore={<ARViewerIcon aria-hidden="true" />}
          onClick={() => window.open(`${BASE_URL}${publicUUID}${langSuffix}`)}
          aria-label={`Open AR viewer for ${propName || 'product'} in new window`}
          style={
            customButtonStyle?.color
              ? {
                  backgroundColor: 'var(--primary-color)',
                  borderColor: 'var(--primary-color)',
                  ...customButtonStyle,
                  '--button-font-size': customButtonStyle?.fontSize,
                  '--button-padding': customButtonStyle?.padding,
                  ...(customButtonStyle?.padding ? { height: 'auto' } : {}),
                }
              : customButtonStyle
              ? {
                  ...customButtonStyle,
                  '--button-font-size': customButtonStyle?.fontSize,
                  '--button-padding': customButtonStyle?.padding,
                  ...(customButtonStyle?.padding ? { height: 'auto' } : {}),
                }
              : {}
          }
          textClassName={customButtonStyle?.fontSize ? 'custom-button-font-size' : ''}>
          {customButtonText || t('open_link')}
        </Button>
      ) : null}
    </div>
  );
}

export default ARQRcode;
